/**
 * Slice E2 — pure gate-outcome classification for roster hard-blocker telemetry.
 *
 * Records PASS / FAIL / SKIPPED_* without changing any gate's assignment verdict.
 * Callers persist via `record_roster_gate_evaluation_controlled` (best-effort).
 */

export const ROSTER_GATE_OUTCOMES = [
  'PASS',
  'FAIL',
  'SKIPPED_MISSING_INPUT',
  'SKIPPED_NOT_APPLICABLE',
] as const;

export type RosterGateOutcome = (typeof ROSTER_GATE_OUTCOMES)[number];

export type RosterGateEvaluation = {
  gateKey: string;
  outcome: RosterGateOutcome;
  /** Required whenever outcome starts with SKIPPED_. */
  skipReason: string | null;
  context?: Record<string, unknown>;
};

/** Canonical gate keys for certification + classification telemetry. */
export const ROSTER_TELEMETRY_GATE_KEYS = {
  requiredCertifications: 'required_certifications',
  requiredClassification: 'required_classification',
} as const;

export type WorkerCertificationEvidence = {
  requirementCode?: string | null;
  certificationType?: string | null;
  status: string;
  verificationStatus?: string | null;
  expiresAt?: string | null; // YYYY-MM-DD
};

/**
 * Classify the certification gate for one assignment attempt.
 *
 * - No required codes → SKIPPED_NOT_APPLICABLE
 * - Required codes but zero evidence rows for the worker → SKIPPED_MISSING_INPUT
 *   (NO_CERTIFICATION_EVIDENCE) — the Medea Park "1,714 unclearable warnings"
 *   case must never look like PASS
 * - Evidence present but does not cover requirements → FAIL
 * - All required codes covered by current evidence → PASS
 */
export function evaluateCertificationGate(input: {
  requiredCertifications: readonly string[] | null | undefined;
  workerCertifications: readonly WorkerCertificationEvidence[] | null | undefined;
  shiftDate: string;
}): RosterGateEvaluation {
  const required = (input.requiredCertifications ?? [])
    .map((c) => c.trim())
    .filter((c) => c.length > 0);

  if (required.length === 0) {
    return {
      gateKey: ROSTER_TELEMETRY_GATE_KEYS.requiredCertifications,
      outcome: 'SKIPPED_NOT_APPLICABLE',
      skipReason: 'NO_CERTIFICATION_REQUIREMENT',
      context: { requiredCount: 0 },
    };
  }

  const evidence = input.workerCertifications ?? [];
  if (evidence.length === 0) {
    return {
      gateKey: ROSTER_TELEMETRY_GATE_KEYS.requiredCertifications,
      outcome: 'SKIPPED_MISSING_INPUT',
      skipReason: 'NO_CERTIFICATION_EVIDENCE',
      context: { requiredCount: required.length, evidenceCount: 0 },
    };
  }

  const shiftDate = input.shiftDate;
  const missing: string[] = [];
  for (const req of required) {
    const ok = evidence.some((cert) => {
      const codeMatch =
        (cert.requirementCode && cert.requirementCode.toUpperCase() === req.toUpperCase()) ||
        (cert.certificationType && cert.certificationType.toUpperCase() === req.toUpperCase());
      if (!codeMatch) return false;
      if (cert.status.toUpperCase() !== 'CURRENT') return false;
      const verification = (cert.verificationStatus ?? 'VERIFIED').toUpperCase();
      if (!['VERIFIED', 'UNVERIFIED', 'REQUIRES_REVIEW'].includes(verification)) return false;
      if (cert.expiresAt && cert.expiresAt < shiftDate) return false;
      return true;
    });
    if (!ok) missing.push(req);
  }

  if (missing.length > 0) {
    return {
      gateKey: ROSTER_TELEMETRY_GATE_KEYS.requiredCertifications,
      outcome: 'FAIL',
      skipReason: null,
      context: { missing, evidenceCount: evidence.length },
    };
  }

  return {
    gateKey: ROSTER_TELEMETRY_GATE_KEYS.requiredCertifications,
    outcome: 'PASS',
    skipReason: null,
    context: { requiredCount: required.length, evidenceCount: evidence.length },
  };
}

/**
 * Classify the classification gate for one assignment attempt.
 *
 * - No required classification → SKIPPED_NOT_APPLICABLE
 * - Required but worker has no classification evidence → SKIPPED_MISSING_INPUT
 * - Required present and worker evidence does not satisfy → FAIL
 * - Satisfied → PASS
 *
 * Matching is level-token aware: a required value of `MA000100:HOME_CARE_AGED:HCA3`
 * or bare `HCA3` both accept a worker level of `HCA3` (see migration 20270824000000).
 * Telemetry must not pretend a fully-qualified string mismatch is a green pass.
 */
export function evaluateClassificationGate(input: {
  requiredClassification: string | null | undefined;
  workerClassification: string | null | undefined;
  workerRole?: string | null | undefined;
}): RosterGateEvaluation {
  const required = (input.requiredClassification ?? '').trim();
  if (!required) {
    return {
      gateKey: ROSTER_TELEMETRY_GATE_KEYS.requiredClassification,
      outcome: 'SKIPPED_NOT_APPLICABLE',
      skipReason: 'NO_CLASSIFICATION_REQUIREMENT',
    };
  }

  const worker = (input.workerClassification ?? '').trim();
  const role = (input.workerRole ?? '').trim();
  if (!worker && !role) {
    return {
      gateKey: ROSTER_TELEMETRY_GATE_KEYS.requiredClassification,
      outcome: 'SKIPPED_MISSING_INPUT',
      skipReason: 'NO_CLASSIFICATION_EVIDENCE',
      context: { required },
    };
  }

  if (classificationTokensMatch(required, worker) || classificationTokensMatch(required, role)) {
    return {
      gateKey: ROSTER_TELEMETRY_GATE_KEYS.requiredClassification,
      outcome: 'PASS',
      skipReason: null,
      context: { required, worker, role },
    };
  }

  return {
    gateKey: ROSTER_TELEMETRY_GATE_KEYS.requiredClassification,
    outcome: 'FAIL',
    skipReason: null,
    context: { required, worker, role },
  };
}

function classificationTokensMatch(required: string, candidate: string): boolean {
  if (!candidate) return false;
  if (required === candidate) return true;
  const reqTokens = tokeniseClassification(required);
  const candTokens = tokeniseClassification(candidate);
  if (reqTokens.size === 0 || candTokens.size === 0) return false;
  // Any shared level-like token counts (HCA3 from fully-qualified MA000100:…).
  for (const t of reqTokens) {
    if (candTokens.has(t)) return true;
  }
  return false;
}

function tokeniseClassification(value: string): Set<string> {
  return new Set(
    value
      .split(/[:/|,;\s]+/)
      .map((t) => t.trim().toUpperCase())
      .filter((t) => t.length > 0),
  );
}

/** Invariant: SKIPPED_* always carries a non-empty skip_reason. */
export function gateEvaluationIsValid(evaluation: RosterGateEvaluation): boolean {
  if (!ROSTER_GATE_OUTCOMES.includes(evaluation.outcome)) return false;
  if (evaluation.outcome.startsWith('SKIPPED')) {
    return Boolean(evaluation.skipReason && evaluation.skipReason.trim().length > 0);
  }
  return evaluation.skipReason == null || evaluation.skipReason === '';
}

export type GateCoverageRow = {
  gateKey: string;
  evaluations: number;
  passes: number;
  fails: number;
  skips: number;
  skipsByReason: Record<string, number>;
  /** Eligible assignment attempts for this gate (= evaluations, including skips). */
  denominator: number;
};

/**
 * Build per-gate coverage from evaluation rows. Known gates with zero rows
 * appear with denominator 0 — never reportable as "green" (all-pass).
 */
export function buildRosterGateCoverage(
  evaluations: readonly RosterGateEvaluation[],
  knownGateKeys: readonly string[] = Object.values(ROSTER_TELEMETRY_GATE_KEYS),
): GateCoverageRow[] {
  const byKey = new Map<string, GateCoverageRow>();

  for (const key of knownGateKeys) {
    byKey.set(key, {
      gateKey: key,
      evaluations: 0,
      passes: 0,
      fails: 0,
      skips: 0,
      skipsByReason: {},
      denominator: 0,
    });
  }

  for (const ev of evaluations) {
    let row = byKey.get(ev.gateKey);
    if (!row) {
      row = {
        gateKey: ev.gateKey,
        evaluations: 0,
        passes: 0,
        fails: 0,
        skips: 0,
        skipsByReason: {},
        denominator: 0,
      };
      byKey.set(ev.gateKey, row);
    }
    row.evaluations += 1;
    row.denominator += 1;
    if (ev.outcome === 'PASS') row.passes += 1;
    else if (ev.outcome === 'FAIL') row.fails += 1;
    else if (ev.outcome.startsWith('SKIPPED')) {
      row.skips += 1;
      const reason = ev.skipReason ?? 'UNKNOWN';
      row.skipsByReason[reason] = (row.skipsByReason[reason] ?? 0) + 1;
    }
  }

  return [...byKey.values()].sort((a, b) => a.gateKey.localeCompare(b.gateKey));
}
